// @ts-strict-ignore
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import {
  LMConfigurationService,
  LMModalWrapperService,
  LMUrlUtilService
} from '@app/services';
import { SohoSwapListComponent } from 'ids-enterprise-ng';
import { LMDialogButtonKey, LMUniqueIdService } from '../../../utils';

@Component({
  selector:    'lm-cube-dimensions-dialog',
  standalone: false,
  templateUrl: 'lm-cube-dimensions-dialog.component.html',
  styleUrls:   [ 'lm-cube-dimensions-dialog.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
  })

// export type LMCubeDimensionParent = 'cubeviewer' | 'reportpersonalization';

export class LMCubeDimensionsDialogComponent implements OnInit {
  @Input() parentContainer: string;
  // used by create report from banner
  @Input() businessClass: string;
  @Input() listName: string;
  @Input() userfolderId: string;
  // used by create report from menu, personalizate list and report, dimension tree
  @Input() listSpec: LMListSpec;
  @Input() listState: LMListState;
  @Input() currentDimensions: LMDimension[];
  @Input() availableDimensions: LMDimension[];
  @Input() dvsLastFixedIndex: number;
  @Input() dvsShowLevels: number;

  public labelAvailable: string;
  public labelSelected: string;
  public labelLevels: string;
  public labelSuppressZeros: string;
  public labelSuppressCurPeriod: string;
  public labelShowTotal: string;
  public labelPivotName: string;
  public labelPreservePD: string;

  private periodDimCheck: string;
  private periodDimCheck2: string;
  private periodDimCheck3: string;
  private fixedDimCheck: string;
  private periodViewCheck: string;
  private pobdDimCheck: string;
  private dbmDimCheck: string;
  private singleDimCheck: string;

  private cubeState: LMCubeOverrides;
  private cubeOptions: LMCubeOptions;

  public available: Array<SohoSwapListItem> = [];
  public current: Array<SohoSwapListItem> = [];

  public pivotName: string;
  public maxLevels: number;
  public showLevels: number;
  public showTotal: boolean;
  public suppressZeros: boolean;
  public suppressCurPeriod: boolean;
  public preservePD: boolean;

  public hasDimensionLevels: boolean;
  public hasTotalNode: boolean;
  public hasSuppressZeros: boolean;
  public hasSuppressCurPeriod: boolean;
  public showPreservePD: boolean;

  private periodDimensions: Array<string> = [];
  private POBDDimensions: Array<string> = [];
  private hasPeriodView: boolean;
  private lastFixedIndex: number;
  private dbmDimensions: string;

  private updateState: boolean;
  @ViewChild(SohoSwapListComponent, { static: true }) swapListComponent: SohoSwapListComponent;

  constructor(
    private lmUniqueIdService: LMUniqueIdService,
    private lmConfigurationService: LMConfigurationService,
    private lmUrlUtilService: LMUrlUtilService,
    private changeDetectionRef: ChangeDetectorRef,
    private lmModalWrapperService: LMModalWrapperService
  ) {
  }

  ngOnInit() {
    if (!this.listSpec) {
      // Create report from banner
      this.lmConfigurationService.getCubeInfo(this.lmUrlUtilService.getReportUrl(this.businessClass), this.businessClass, this.listName, this.userfolderId).subscribe((response: LMCubeOptions) => {
        this.cubeOptions = response;
        this.listState = {};
        this.cubeState = {};
        this.initialize();
      });
    } else {
      this.cubeState = this.listState?.cubeOverrides || {};
      this.cubeOptions = this.listSpec?.cubeOptions || null;
      this.initialize();
    }
  }

  private initialize(): void {
    this.labelAvailable = Soho.Locale.translate('AvailableDimensions');
    this.labelSelected = Soho.Locale.translate('DimensionTree');
    this.labelLevels = Soho.Locale.translate('ShowDimensionLevels');
    this.labelSuppressZeros = Soho.Locale.translate('SuppressZeros');
    this.labelSuppressCurPeriod = Soho.Locale.translate('SuppressCurrentPeriod');
    this.labelPreservePD = Soho.Locale.translate('PreservePivotDimensionsWhenFiltered');
    this.labelShowTotal = Soho.Locale.translate('ShowTotalNode');
    this.periodDimCheck = Soho.Locale.translate('PeriodDimensionCheck');
    this.periodDimCheck2 = Soho.Locale.translate('PeriodDimensionCheck2');
    this.periodDimCheck3 = Soho.Locale.translate('PeriodDimensionCheck3');
    this.fixedDimCheck = Soho.Locale.translate('FixedDimensionCheck');
    this.periodViewCheck = Soho.Locale.translate('PeriodViewCheck');
    this.pobdDimCheck = Soho.Locale.translate('pobdCheck');
    this.dbmDimCheck = Soho.Locale.translate('dbmDimensionCheck');
    this.singleDimCheck = Soho.Locale.translate('singleDimensionCheck');

    const hasOverride = !!this.cubeState?.dimensionOverrides?.overrideDims;
    const fromBanner = !this.currentDimensions; //if create report from banner, there is no input for current dimension

    if (!!this.cubeOptions.pivotViews) {
      if (this.cubeState?.pivotView) {
        this.pivotName = this.cubeState?.pivotView;
      } else {
        let pivotView = this.cubeOptions.pivotViews.find((p) => p.default !== undefined && p.default);

        if (!pivotView) {
          pivotView = this.cubeOptions.pivotViews[ 0 ];
        }

        this.pivotName = pivotView.name;
      }
    }

    if (!!this.cubeOptions.columnViews) {
      let columnView;
      if(this.cubeState?.columnView) {
        columnView = this.cubeOptions.columnViews.find(c => c.name === this.cubeState.columnView);
      } else {
        columnView = this.cubeOptions.columnViews.find(c => c.default !== undefined && c.default);
        if (!columnView) {
          columnView = this.cubeOptions.columnViews[0];
        }
      }
      this.dbmDimensions = columnView.dbmDimensions;
    } else if (!!this.cubeOptions.cubeDisplayFields) {
      this.dbmDimensions = this.cubeOptions.cubeDisplayFields.dbmDimensions;
    }

    let currentDimsArray: LMDimension[] = [];
    let availableDimsArray: LMDimension[] = [];

    if (this.currentDimensions) { // currentDimensions should atleast have 1 dimension
      currentDimsArray = this.currentDimensions;
      availableDimsArray = this.availableDimensions;
    } else {
      // create report from banner
      currentDimsArray = this.cubeOptions.currentDims;
      availableDimsArray = this.cubeOptions.availableDims;
    }

    // for cube viewer only: showLevels, showTotal, preservePD
    // available to all: suppressZeros, suppressCurPeriod, lastFixedIndex
    this.showLevels = this.dvsShowLevels || 0; // Used by both cube and tree. Not replicated in CubeInfo.
    this.showTotal = hasOverride ? this.cubeState.dimensionOverrides.showTotal : this.cubeOptions?.showTotal;
    this.suppressZeros = hasOverride ? this.cubeState.dimensionOverrides.suppressZeros : this.cubeOptions?.suppressZeros;
    this.suppressCurPeriod = hasOverride ? this.cubeState.dimensionOverrides.suppressCurPeriod : this.cubeOptions?.suppressCurPeriod;
    this.preservePD = hasOverride ? this.cubeState.dimensionOverrides.preservePD : false;
    this.maxLevels = (this.showTotal) ? currentDimsArray.length + 1 : currentDimsArray.length;

    if (this.pivotName) {
      this.labelPivotName = Soho.Locale.translate('PivotView') + ': ' + this.cubeOptions.pivotViews?.find((p) => p.name === this.pivotName).label;
    }

    this.hasPeriodView = (this.cubeOptions.periods !== undefined);
    this.hasSuppressCurPeriod = (this.cubeOptions?.hasPeriodDimension && !this.hasPeriodView) || this.parentContainer === 'reportpersonalization';
    this.hasDimensionLevels = this.parentContainer === 'cubeviewer';
    this.hasTotalNode = this.parentContainer === 'cubeviewer';
    this.hasSuppressZeros = true;
    this.showPreservePD = this.parentContainer === 'cubeviewer' && (this.listSpec.busListSpec.hasChildHierarchyDims || this.listSpec.busListSpec.searchForm !== undefined);
    this.lastFixedIndex = !fromBanner ? this.dvsLastFixedIndex : this.cubeOptions.lastFixedIndex;

    availableDimsArray.map(m => {
      this.available.push({ id: m.id, value: m.id, text: m.label });
    });

    currentDimsArray.map(m => {
      this.current.push({ id: m.id, value: m.id, text: m.label });
    });

    availableDimsArray.filter(availableDim => availableDim?.isPeriod).forEach(item => this.periodDimensions.push(item.id));
    currentDimsArray.filter(currentDim => currentDim.isPeriod).forEach(item => this.periodDimensions.push(item.id));
    currentDimsArray.filter(currentDim => currentDim.isPeriod && currentDim?.isPOBD).forEach(item => this.POBDDimensions.push(item.id));
    if (this.POBDDimensions.length > 0) {
      this.hasSuppressCurPeriod = false;
    }

    this.updateState = false;
    //TODO: not sure why but when creating report from banner, passing available and current items as Input does not work
    this.swapListComponent.availableItems = this.available;
    this.swapListComponent.selectedItems = this.current;
    this.changeDetectionRef.detectChanges();
  }

  public onBeforeSwap(event: SohoSwapListBeforeSwapEvent) {
    if (!event.moved.items || event.moved.items.length === 0) {
      event.result = false;
      return;
    }
    if (this.listSpec.busListSpec.implementsSingleDimension) {
      this.printMessage(this.singleDimCheck);
      event.result = false;
      return;
    }

    // Before swap, validate the dimension tree.
    const swapItemId = event.moved.items[ 0 ].id;
    const filteredItem = this.swapListComponent.availableItems.filter((availableItem) => availableItem.id === swapItemId);
    if (filteredItem.length > 0) {
      // Can the selected items be added to the dimension tree?
      const thisArg = this;
      const periodItemsCount = event.moved.items.filter((item) => thisArg.periodDimensions.findIndex((period) => period === item.id) > -1).length;

      // The cubes can have more than one period dimension.
      // A period dimension is prevented from being added to the dimension tree if the CubeView has period Views.
      if (this.hasPeriodView && periodItemsCount > 0) {
        this.printMessage(this.periodViewCheck);
        event.result = false;
        return;
      }

      // Cannot move a Period Dimension since the current dimension tree has a period operator based dimension
      if (this.POBDDimensions.length > 0 && periodItemsCount > 0) {
        this.printMessage(this.periodDimCheck3);
        event.result = false;
        return;
      }

      // LMWEB-17676 - Prevent CubeView pivot from containing Dimension Based Measure dimensions
      // Current column view has dimension based measures. Cannot move a Dimension based measure dimension to the current dimension tree.
      if (this.dbmDimensions?.length > 0) {
        let found = false;
        event.moved.items.every(item => {
          if (this.dbmDimensions.indexOf(item.id) > -1) {
            this.printMessage(this.dbmDimCheck);
            found = true;
            return false; // breaks items.every loop
          }
          return true; // continues items.every loop
        });
        if (found) {
          event.result = false;
          return;
        }
      }

      // *** This Check is not performed since the introduction of multiple period dimension feature
      // 1. Check the number of period dimensions in event items list
      // if (periodItemsCount > 1) {
      //   // Cannot select more than one period dimension
      //   this.printMessage(this.periodDimCheck);
      //   event.result = false;
      //   return;
      // }

      // *** This Check is not performed since the introduction of multiple period dimension feature
      // 2. Is there a period dimension in the selected items list already?
      // if (periodItemsCount === 1) {
      //   let selectedPeriodItems = this.swapListComponent.selectedItems.filter((selectedItem) => thisArg.periodDimensions.findIndex((period) => period === selectedItem.id) > -1);
      //   if (selectedPeriodItems.length > 0 ) {
      //     this.printMessage(this.periodDimCheck2);
      //     event.result = false;
      //     return;
      //   }
      // }
    } else {
      // Can the selected items be removed from the dimension tree?
      // 3. Fixed dimensions cannot be removed from the selected list.
      const firstSelectedIndex = this.swapListComponent.selectedItems.findIndex(selectedItem => selectedItem.id === event.moved.items[ 0 ].id);
      if (firstSelectedIndex <= this.lastFixedIndex) {
        this.printMessage(this.fixedDimCheck);
        event.result = false;
        return;
      }

      let found = false;
      event.moved.items.every(item => {
        const itemPOBD = this.POBDDimensions.filter(p => p === item.id);
        if (itemPOBD.length) {
          // Cannot swap POBD dimension
          this.printMessage(this.pobdDimCheck);
          event.result = false;
          found = true;
          return false; //breaks every loop
        }
        return true; //continues looping
      });
      if (found) {
        return;
      }
    }
    event.result = true;
  }

  private printMessage(message: string) {
    const dialogId = 'DimensionTree';
    const buttons = [
      // tslint:disable-next-line - shadowed variable e
      {
        id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CLOSE ]),
        text:  Soho.Locale.translate(LMDialogButtonKey.CLOSE),
        click: (e, modal) => {
          modal.close();
        }
      }
    ];
    this.lmModalWrapperService
      .message()
      .message(message)
      .title(Soho.Locale.translate(dialogId))
      .buttons(buttons)
      .open();
  }

  public onDimensionSwapListUpdated(event: SohoSwapListSwapUpdateEvent) {
    // Swap list updated - Get the new dimension tree.
    // let updated = this.swapListComponent.selectedItems;
    this.maxLevels = (this.showTotal) ? this.swapListComponent.selectedItems.length + 1 : this.swapListComponent.selectedItems.length;
    this.updateState = true;

    // ListState is not set for report and personalization and there are no filtered dimensions in that case.
    if (this.listState?.cubeOverrides?.fltrDimensions?.length) {
      const dimensionTree = this.swapListComponent.selectedItems.map(m => m.id).join(',');
      const filteredDimensions = this.listState.cubeOverrides.fltrDimensions.filter(dim => dimensionTree.indexOf(dim.fd) >= 0);
      if (filteredDimensions.length > 0 && !this.preservePD) {
        this.printMessage(Soho.Locale.translate('SetPreservePivotDimensionWhenFiltered'));
      }
    }
  }

  public onDirty(event: any) {
    this.updateState = true;
  }

  public onPristine(event: any) {
    // Checkbox unchecked calls onPristine. The value is changed from the initial state, so set updateState to true.
    this.updateState = true;
  }

  // This is used by CubeViewer only.
  // If the cube has dimension overrides, reset it. Boolean return will determine if the cube has to be reloaded.
  public onReset(): boolean {
    if (Object.keys(this.listState).length > 0 && this.listState.hasOwnProperty('cubeOverrides')) {
      this.listState.cubeOverrides.dimensionOverrides = {};
      return true;
    }
    return false;
  }

  // This is used by CubeViewer only
  // If the current state is modified, update cubeOverrides. Boolean return will determine if the cube has to be reloaded.
  public onApply(): boolean {
    if (this.updateState) {
      this.listState.cubeOverrides = this.listState.cubeOverrides || {};
      this.listState.cubeOverrides.dimensionOverrides = this.getCubeOverrides();
    }
    return this.updateState;
  }

  private getCubeOverrides(): LMCubeDimensionOverrides {
    const cubeDimensionOverrides: LMCubeDimensionOverrides = {};

    const dimensionTree = this.swapListComponent.selectedItems.map(m => m.id).join(',');
    cubeDimensionOverrides.dimensionsOverride = dimensionTree;
    cubeDimensionOverrides.showLevels = this.showLevels;
    cubeDimensionOverrides.suppressZeros = this.suppressZeros;
    cubeDimensionOverrides.showTotal = this.showTotal;
    cubeDimensionOverrides.suppressCurPeriod = this.suppressCurPeriod;
    cubeDimensionOverrides.preservePD = this.preservePD;
    cubeDimensionOverrides.overrideDims = true;

    return cubeDimensionOverrides;
  }

  // Used by report and personalization dialog.
  // The cube overrides object will hold the current selection based on which a report or personalization will be created.
  public updateCubeState(): LMListState {
    // based on hasCubeDimensionsChanged
    // Used in create report and personalization
    this.listState.cubeOverrides = this.cubeState || {};
    this.listState.cubeOverrides.dimensionOverrides = {};
    const dimOverrideArray = this.cubeState?.dimensionOverrides?.dimensionsOverride?.split(',') || [];
    let selectedItemsChanged = dimOverrideArray.length !== this.swapListComponent.selectedItems.length;
    if (!selectedItemsChanged) {
      this.swapListComponent.selectedItems.forEach((m, index) => {
        if (m.id !==  dimOverrideArray[index]) {
          selectedItemsChanged = true;
        }
      });
    }

    if (selectedItemsChanged) {
      this.listState.cubeOverrides.dimensionOverrides.dimensionsOverride = this.swapListComponent.selectedItems.map(m => m.id).join(',');
      this.listState.cubeOverrides.pivotView = this.pivotName || undefined;
    }

    if (this.suppressZeros !== this.cubeState.dimensionOverrides?.suppressZeros) {
      this.listState.cubeOverrides.dimensionOverrides.suppressZeros = this.suppressZeros;
    }

    if (this.suppressCurPeriod !== this.cubeState?.dimensionOverrides?.suppressCurPeriod) {
      this.listState.cubeOverrides.dimensionOverrides.suppressCurPeriod = this.suppressCurPeriod;
    }

    return this.listState;
  }
}
